import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import constate from "constate";
import { useCallback, useEffect, useState } from "react";

import { useGetFocusModeStatus } from "../api/useGetFocusModeStatus";
import { useOptInToFocusMode } from "../api/useOptInToFocusMode";
import { FOCUS_MODE_STATUS_POLL_INTERVAL_MS } from "../constants";
import { useFocusModeTracking } from "./useFocusModeTracking";

interface UseFocusModeStateParams {
  shiftId: string;
  isOnBreak?: boolean;
}

function useFocusModeState({ shiftId, isOnBreak = false }: UseFocusModeStateParams) {
  const [isOptedIn, setIsOptedIn] = useState(false);
  const [focusModeSessionId, setFocusModeSessionId] = useState("");
  const [hasViolations, setHasViolations] = useState(false);
  const [bonusPoints, setBonusPoints] = useState(0);
  const [shiftEndAt, setShiftEndAt] = useState("");

  const { data: statusResponse } = useGetFocusModeStatus(
    { shiftId },
    { refetchInterval: isOptedIn ? FOCUS_MODE_STATUS_POLL_INTERVAL_MS : undefined }
  );

  const statusData = statusResponse?.data;

  const { mutateAsync: optInMutation } = useOptInToFocusMode();

  // Recover state from backend on mount (e.g., after app restart)
  useEffect(() => {
    if (statusData?.isOptedIn) {
      setIsOptedIn(true);
      setFocusModeSessionId(statusData.focusModeSessionId);
      setHasViolations(statusData.hasViolations);
      setBonusPoints(statusData.bonusPoints);
    }
  }, [statusData]);

  const isActive = isOptedIn && statusData?.status === "active";

  const optIn = useCallback(async () => {
    const response = await optInMutation({ shiftId });
    const optInData = response.data.data;
    setIsOptedIn(true);
    setFocusModeSessionId(optInData.focusModeSessionId);
    setBonusPoints(optInData.bonusPoints);
    setShiftEndAt(optInData.shiftEndAt);

    logEvent(APP_V2_USER_EVENTS.FOCUS_MODE_OPTED_IN, {
      shiftId,
      focusModeSessionId: optInData.focusModeSessionId,
      bonusPoints: optInData.bonusPoints,
    });
  }, [shiftId, optInMutation]);

  useFocusModeTracking({
    shiftId,
    focusModeSessionId,
    isActive,
    isOnBreak,
  });

  return {
    isOptedIn,
    isActive,
    hasViolations,
    bonusPoints,
    shiftEndAt,
    focusModeSessionId,
    currentStatus: statusData?.status,
    optIn,
  };
}

export const [FocusModeProvider, useFocusModeContext] = constate(useFocusModeState);
